'use client'

import type { ReactNode } from 'react'

import type { ImageSlotEntry } from '@/lib/content-edit/sources'

import { useContentEdit } from './content-edit-provider'

interface EditableImageProps {
  slot: string
  current: ImageSlotEntry | null
  children: ReactNode
}

/**
 * Click-to-replace wrapper for an image slot.
 *
 * With edit mode off (every visitor, always on production) this renders its
 * children and nothing else: no wrapper element, no handlers, no extra DOM.
 * Edit mode adds a relatively-positioned wrapper and a full-cover native
 * button that opens the upload drawer. The button is native rather than a
 * HeroUI Button because it has to stretch to the image box; a HeroUI Button
 * would impose its own padding and pill shape.
 */
export function EditableImage({ slot, current, children }: EditableImageProps) {
  const { editing, openImageDrawer } = useContentEdit()

  if (!editing) return <>{children}</>

  return (
    <div className='relative' data-edit-slot={slot}>
      {children}
      <button
        type='button'
        onClick={() => openImageDrawer(slot, current)}
        aria-label={`Replace image ${slot}`}
        className='absolute inset-0 flex items-center justify-center rounded-md bg-black/50 text-sm font-semibold text-white opacity-0 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent'
      >
        Replace image
      </button>
    </div>
  )
}
